import React from "react";
import memoize from "@emotion/memoize";
import { props } from "@styled-system/should-forward-prop";

import {
  color,
  compose,
  css,
  EmotionComponent,
  focusOutlineProp,
  hoverColorProp,
  layout,
  OverrideNativeElementProps,
  space,
  styled,
  theme,
} from "@catchoftheday/theme";

import { BaseIconProps, IconProps } from "../types";

const defaultViewBox = "0 0 34 30";

const StyledSVG = styled.svg`
  vertical-align: middle;
  shape-rendering: inherit;
`;

const BaseIcon: React.FC<OverrideNativeElementProps<BaseIconProps, "svg">> =
  React.forwardRef(
    ({ name, viewBox, path, paths, children, title, ...props }, ref) => {
      return (
        <StyledSVG
          viewBox={viewBox || defaultViewBox}
          fill="currentcolor"
          ref={ref}
          {...props}
        >
          {title && <title>{title}</title>}
          {paths && paths.length ? (
            paths.map((path, idx) => (
              <path key={`${name}-path-${idx}`} d={path.d} {...path.props} />
            ))
          ) : (
            <>{path && <path d={path} />}</>
          )}
          {children}
        </StyledSVG>
      );
    }
  );

BaseIcon.displayName = "BaseIcon";

const styleProps = compose(layout, space, color);

export const Icon: EmotionComponent<IconProps> = styled(BaseIcon, {
  shouldForwardProp: memoize(
    (prop) => !new RegExp(`^(${props.join("|")})$`).test(prop)
  ),
})<IconProps>`
  outline: 0;

  ${(props) =>
    props.hoverColor &&
    css`
      :hover {
        ${hoverColorProp(props)}
      }
    `}
  ${(props) =>
    props.focusOutline &&
    css`
      &:focus[data-focus-visible-added],
      &:focus-visible {
        ${focusOutlineProp(props)};
        outline-offset: 4px;
      }
    `}
  ${styleProps}
`;

Icon.displayName = "Icon";

Icon.defaultProps = {
  height: "24px",
  width: "24px",
  focusOutline: `1px solid ${theme.colors.secondary}`,
};

export function createSvgIconComponent(
  options?: IconProps
): React.FC<IconProps> {
  const Component = (props) => <Icon {...options} {...props} />;

  Component.displayName = "Icon";

  return Component;
}
